/* A finished game's snapshots, in the shapes the game view needs them in.

   The replay is the game view in review mode (owner's ask, 2026-09-07 — it used to be a view of
   its own, with a bar and exits of its own): `hooks/useReplay` steps through the snapshots and
   loads each into the store as though it were the live game, and these are the pure helpers it
   and the replay route share. */

import { buildTimeline } from "src/utils/roundMotion"
import { activeWorld } from "src/utils/world"

// Where a replay looks when nobody owned anything: the middle of the world — of whichever world
// is active (`utils/world`), at the point [125, 45] was on L.
const worldCentre = () => {
  const { columns, rows } = activeWorld()
  return [Math.round(columns * 0.625), Math.round(rows * 0.45)]
}

/* The ticks that can actually be drawn. Summaries exist for every round for ever; full maps only
   inside the retention window, and pruned ones store `{}` on purpose. */
export const playableOf = (data) =>
  (data?.steps || []).filter((step) => Object.keys(step.metadata || {}).length > 0)

/* Where the replay opens: the middle of everything anybody owned in the first snapshot.

   It used to be the *first* owned cell, which is whichever seat the serializer happened to write
   first — so the camera opened on one player's home with the other off to one side. On a 1140px
   map that only looks careless; on a phone the pane is 390x439, and in the game this was found on
   the two sides sat twelve rows apart, so opening on one of them put the other below the fold and
   the first thing a replay showed was empty ocean.

   Exported and pure for the reason `pinchTo` is: it is the only arithmetic here, and a camera
   wrong by half a pane looks plausible until you know where the armies were. */
export const cameraCentre = (step) => {
  const owned = Object.entries(step?.metadata?.cells || {}).filter(([, cell]) => cell.owner != null)
  if (!owned.length) return worldCentre()
  const columns = owned.map(([coordinates]) => Number(coordinates.split("-")[0]))
  const rows = owned.map(([coordinates]) => Number(coordinates.split("-")[1]))
  return [
    Math.round((Math.min(...columns) + Math.max(...columns)) / 2),
    Math.round((Math.min(...rows) + Math.max(...rows)) / 2),
  ]
}

/* One snapshot, in the raw shape `setGame` reduces — so the store holds a replay exactly as it
   holds a live game, and every panel below the map is the one the game mounts.

   The steps endpoint carries less than the game socket does, and each gap is filled with the value
   that makes a panel read correctly rather than with a guess: nobody is connected (a replay has no
   sockets), nobody is ready (there is no round to wait for), and there is no host, because the one
   thing a host is for is advancing a clock that has already stopped. `initial_locations` is absent
   and does not need to be: `crownCells` falls back to the snapshot's own `users` block, which is
   what makes the crowns fade exactly when the replay's war took them. */
export const gameFromStep = (data, step) => ({
  id: data.game_id,
  status: data.status,
  winner_team: data.winner_team,
  // Who outlasted everybody, in a survival replay (SV14). The steps endpoint carries it beside
  // the winning side, and the result screen a finished replay opens on reads it by name.
  winner_user: data.winner_user ?? null,
  mode: data.mode,
  // The enemy's difficulty, so a survival replay's roster names the waves it was played against
  // rather than falling back to whatever the first bot happens to be.
  survival_difficulty: data.survival_difficulty ?? null,
  ranked: data.ranked ?? null,
  // Which world it was played on (requirement 35): the steps payload names it, and every snapshot
  // records it too; a replay from before the sizes is L, as the game was.
  map_size: data.map_size ?? step.metadata?.map_size ?? "L",
  tick: step.tick,
  tick_time: step.datetime,
  round_seconds: null,
  host: { id: null, username: "", ranking_points: 0 },
  paused_by: null,
  state: step.metadata,
  players: (data.players || []).map((seat) => ({
    user: {
      id: seat.id,
      username: seat.username,
      ranking_points: seat.ranking_points ?? 0,
      is_bot: seat.is_bot,
      // The league now (owner, 2026-10-08): the result screen frames each look with it.
      league: seat.league ?? null,
      // The player's picture (owner, 2026-10-09), in the shape `avatarFor` reads off a live seat.
      equipped_cosmetics: seat.avatar ? { avatar: seat.avatar } : undefined,
    },
    team: seat.team,
    race: seat.race,
    // The rung this seat played at, beside the class it played (requirement 31, 2026-09-20): the
    // steps endpoint carries it on the seat row, which is where the game payload carries it too.
    bot_difficulty: seat.bot_difficulty,
    // The colour the seat played in. It comes off the game's own Player row, so a replay is drawn
    // in the colours its lobby agreed on — dropping it redrew every seat on a side as
    // `<team>_default`, and a game played in four colours replayed in two.
    unit_icon: seat.unit_icon,
    ready: false,
    connected: false,
    initial_locations: [],
    // Dated falls are the record (SV14): a seat with an `eliminated_at_tick` fell, and the
    // verdict screen's enemy tally counts it. `false` here — a replay has no live crown count
    // to read — made every finished game read "N enemies · 0 fallen" the moment the steps
    // arrived, live game-over screens included, since a finished game flips into review.
    eliminated: seat.eliminated_at_tick != null,
    /* The seat's verdict and the round its last crown fell (SV14). They come off the game's own
       rows rather than off the snapshot, so they are the same on every tick of the replay — a
       stepper standing on round three still reports the game's result, which is what the RESULT
       button in the bar opens. */
    outcome: seat.outcome ?? null,
    eliminated_at_tick: seat.eliminated_at_tick ?? null,
    ranking_delta: seat.ranking_delta ?? null,
  })),
  spectators: [],
})

/* How long autoplay holds a round (owner, 2026-10-26: "we leave every round as much time as it
   takes for the movements / animations to finish. Then we go to the next round"): the show the
   game view plays as the board lands, the same timeline it builds, then `REPLAY_REST_MS` so the
   board it ends on can be read. Never less than `REPLAY_BEAT_MS`, the old fixed beat, which is
   also all a board with no log gets, and all a viewer gets who asks for stillness (no show). */
export const REPLAY_BEAT_MS = 900
export const REPLAY_REST_MS = 600

export const roundHoldMs = (step, { viewerId = null, still = false, speed = 1 } = {}) => {
  const motion = step?.metadata?.motion
  if (still || !motion?.steps?.length) return REPLAY_BEAT_MS
  const teamOf = (id) => step.metadata.users?.[String(id)]?.team
  const { total } = buildTimeline(motion, { columns: activeWorld().columns, teamOf, viewerId })
  // At x2 or x4 the show is that much shorter (M10); the rest to read the board is not.
  return Math.max(REPLAY_BEAT_MS, total / (speed || 1) + REPLAY_REST_MS)
}
